import { ImageIcon, PlusInSquareIcon } from "assets/icons";
import { If } from "components";
import { useBoolean } from "hooks";
import { t } from "i18next";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { useState } from "react";
import { Button, InfoTooltipIcon, Pagination } from "ui";

import {
  ScheduleModal,
  SharingSchedulesTable,
  DeleteSharingScheduleModal,
  SharingSchedulesModeControl,
  SharingsSectionStyleModal
} from "../../components";
import { useSharingSchedulesList } from "../../hooks";
import { SharingSchedulesListItem } from "../../sharing.types";

import styles from "./SharingSchedule.module.scss";

const SharingSchedule = () => {
  const {
    sharingSchedules,
    isLoading,
    refetchSharingSchedules,
    sorting,
    setSorting,
    page,
    setPage,
    totalItems
  } = useSharingSchedulesList();

  const {
    value: isScheduleModalOpen,
    setTrue: openScheduleModal,
    setFalse: closeScheduleModal
  } = useBoolean(false);

  const {
    value: isSharingsSectionStyleModalOpen,
    setTrue: openSharingsSectionStyleModal,
    setFalse: closeSharingsSectionStyleModal
  } = useBoolean(false);

  const [editableSchedule, setEditableSchedule] =
    useState<SharingSchedulesListItem | null>(null);

  const [deletingScheduleId, setDeletingScheduleId] = useState<string | null>(
    null
  );

  const handleCloseScheduleModal = () => {
    closeScheduleModal();
    setEditableSchedule(null);
  };

  return (
    <ContentLayout
      title={t("Sharing Schedule")}
      action={
        <div className={styles.headerActions}>
          <SharingSchedulesModeControl />

          <Button icon={<PlusInSquareIcon />} onClick={openScheduleModal}>
            {t("New Schedule")}
          </Button>
        </div>
      }
    >
      <BoxShadowLayout className={styles.pageContent}>
        <div className={styles.pageContent_tableHeader}>
          <Button
            variant="default"
            icon={<ImageIcon />}
            onClick={openSharingsSectionStyleModal}
            rightIcon={
              <InfoTooltipIcon
                tooltipText={t(
                  "Banner and background image for the “Products to Share Today” section in the app."
                )}
              />
            }
          >
            {t("Banner and Background")}
          </Button>
        </div>

        <div className={styles.pageContent_tableContainer}>
          <SharingSchedulesTable
            schedules={sharingSchedules ?? []}
            isLoading={isLoading}
            sorting={sorting}
            setSorting={setSorting}
            onEditSchedule={(schedule) => {
              setEditableSchedule(schedule);
              openScheduleModal();
            }}
            onDeleteSchedule={setDeletingScheduleId}
          />
        </div>

        <Pagination
          currentPage={page}
          onChange={setPage}
          totalItems={totalItems}
        />
      </BoxShadowLayout>

      <If condition={isScheduleModalOpen}>
        <ScheduleModal
          editableSchedule={editableSchedule}
          refetchSchedule={refetchSharingSchedules}
          isOpen={isScheduleModalOpen}
          onClose={handleCloseScheduleModal}
        />
      </If>

      <If condition={!!deletingScheduleId}>
        <DeleteSharingScheduleModal
          id={String(deletingScheduleId)}
          isOpen={Boolean(deletingScheduleId)}
          onDeleteSuccess={refetchSharingSchedules}
          onClose={() => setDeletingScheduleId(null)}
        />
      </If>

      <If condition={isSharingsSectionStyleModalOpen}>
        <SharingsSectionStyleModal
          isOpen={isSharingsSectionStyleModalOpen}
          onClose={closeSharingsSectionStyleModal}
        />
      </If>
    </ContentLayout>
  );
};

export default SharingSchedule;
